import {ArrDrawer} from '../../../../common/arr-drawer/ArrDrawer';
import ArrRadio from '../../../../common/arr-radio/ArrRadio';
import React, {useState, useEffect, useMemo} from 'react';
import {Box, Typography} from '@mui/material';
import './SaveCommunicationPanel.scss';
import ArrButton from '../../../../common/arr-button/ArrButton';
import ArrTextField from '../../../../common/arr-text-field/ArrTextField';
import ArrTreeView from '../../../../common/arr-tree-view/ArrTreeView';
import {useTranslation} from 'react-i18next';
import {
  transition_time_default,
  ToastType,
} from '../../../../../shared/constants';
import {
  useSaveCommunicationExplorerReport,
  useSavedReports,
} from '../../../../../hooks/communicationExplorer/useCommunicationExplorer';
import ArrTooltipWrapper from '../../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as InfoIcon} from '../../../../../assets/images/info-icon.svg';
import ArrToastNotification from '../../../../common/arr-toast-notification/ArrToastNotification';
import ArrModal from '../../../../common/arr-modal/ArrModal';
import {ReportPayload} from '../../FilterHeader';

type SaveCommunicationPanelProps = {
  isOpen: boolean;
  onClose: () => void;
  reportCriteria: ReportPayload | null;
  dataAsOf: string;
  reportId: string | null | number;
};

const SaveCommunicationPanel: React.FC<SaveCommunicationPanelProps> = ({
  isOpen,
  onClose,
  reportCriteria,
  dataAsOf,
  reportId,
}) => {
  const SAVE_AS_NEW = 'new';
  const REPORT_TYPE_PUBLIC = 'public';
  const [action, setAction] = useState(SAVE_AS_NEW);
  const [reportType, setReportType] = useState('private');
  const [reportName, setReportName] = useState('');
  const [selectedItem, setSelectedItem] = useState('');
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const {t} = useTranslation();

  const confirmationMessage = useMemo(() => {
    if (action === SAVE_AS_NEW) {
      return t('communicationExplorer.header.save.publicReportError');
    }
    return t('communicationExplorer.header.save.OverwriteReportError');
  }, [action, t]);

  const {data, refetch, isFetching} = useSavedReports('CER013');
  useEffect(() => {
    if (action === 'overwrite') {
      refetch();
    }
  }, [action, refetch]);
  const closeModal = () => {
    setSelectedItem('');
    setReportName('');
    setAction(SAVE_AS_NEW);
    onClose?.();
  };

  const executeSaveMutation = (isUpdate: boolean) => {
    const name = isUpdate
      ? (treeData
          .flatMap((f) => f.children ?? [])
          .find((r) => r.id === selectedItem)?.label ?? '')
      : reportName.trim();
    const finalReportID = isUpdate ? Number(selectedItem) : reportId;

    if (!reportCriteria) {
      return;
    }
    let dataAsOfISOString = '';
    try {
      dataAsOfISOString = new Date(dataAsOf).toISOString();
    } catch (e) {
      dataAsOfISOString = new Date().toISOString();
      console.error(
        'Failed to convert dataAsOf prop to Date. Using current time.',
        e
      );
    }

    const selectionCriteriaPayload = {
      sourceType: reportCriteria.sourceName,
      sourceID: reportCriteria.source,
      navigationSourceType: reportCriteria.navigationSourceType,

      showByCount: reportCriteria.showByCount,
      showBy: reportCriteria.showBy,
      showByVolume: reportCriteria.showByVolume,

      // Other fixed/standard fields
      docListID: 0,
      flowChart: {},
      isBulkAction: false,
      reportName: name,
      dataAsOf: dataAsOfISOString,
      isDocumentCount: false,
      documentCount: 0,
      totalDocumentToView: 0,
      communicationLiteralValue: [],
      isShared: reportType === 'public',
      isSavedSearchSource: true,
    };

    saveReportMutation.mutate({
      filters: reportCriteria.filters,
      selectionCriteria: selectionCriteriaPayload,
      actionType: 'SAVEREPORT',
      reportType: 'CER013',
      reportName: name,
      updateOrCreate: isUpdate,
      reportId: finalReportID,
    });
  };
  // ---------------- API CALLS ----------------

  const saveReportMutation = useSaveCommunicationExplorerReport(
    (_) => {
      setShowSuccessToast(true);
      setTimeout(() => {
        setShowSuccessToast(false);
        closeModal();
      }, 1500);
    },
    (error) => {
      setErrorMessage(error.message);
      setShowErrorToast(true);
    }
  );
  const handleModalConfirmSave = () => {
    setShowConfirmationModal(false);
    if (action === SAVE_AS_NEW) {
      executeSaveMutation(false);
    } else {
      executeSaveMutation(true);
    }
  };

  const handleModalCancel = () => {
    setShowConfirmationModal(false);
  };

  const handleSave = () => {
    if (action === SAVE_AS_NEW) {
      const trimmedName = reportName.trim();
      const isDuplicate = data?.some(
        (report: {text: string}) =>
          report.text.toLowerCase() === trimmedName.toLowerCase()
      );

      if (isDuplicate) {
        setErrorMessage(
          `${trimmedName} ${t('common.alreadyExists', 'Already Exists')}`
        );
        setShowErrorToast(true);
        return;
      } // PUBLIC REPORT CONFIRMATION CHECK
      if (reportType === REPORT_TYPE_PUBLIC) {
        // Show confirmation modal instead of saving immediately (Flow 1)
        setShowConfirmationModal(true);
        return; // Stop the save flow here
      } // PROCEED TO SAVE (If not duplicate and not public) (Flow 2)
      executeSaveMutation(false);
    } else {
      // Overwrite existing (Flow 3)
      if (!selectedItem) return;
      setShowConfirmationModal(true);
    }
  }; // Transform API → Tree View structure

  const treeData = useMemo(() => {
    if (!data) return []; // Find folder nodes

    const privateFolder = data.find((item) => item.text === 'Private');
    const publicFolder = data.find((item) => item.text === 'Public'); // Find children of each folder based on `parent`

    const privateChildren = data
      .filter((item) => item.parent === privateFolder?.id)
      .map((item) => ({
        id: item.id,
        label: item.text,
      }));

    const publicChildren = data
      .filter((item) => item.parent === publicFolder?.id)
      .map((item) => ({
        id: item.id,
        label: item.text,
      }));

    return [
      {
        id: privateFolder?.id ?? 'private',
        label: t('saveReport.private'),
        disabled: true,
        children: privateChildren,
      },
      {
        id: publicFolder?.id ?? 'public',
        label: t('saveReport.public'),
        disabled: true,
        children: publicChildren,
      },
    ];
  }, [data, t]); /** TreeView selection */

  const handleSelected = (value: string[] | null) => {
    setSelectedItem(value?.[0] ?? '');
  };

  const actionOptions = [
    {label: t('saveReport.overwrite'), value: 'overwrite'},
    {label: t('saveReport.saveAsNew'), value: 'new'},
  ];

  const reportTypeOptions = [
    {label: t('saveReport.private'), value: 'private'},
    {label: t('saveReport.public'), value: 'public'},
  ];

  return (
    <ArrDrawer
      side="right"
      showButton={false}
      isOpen={isOpen}
      onClose={closeModal}
      size="large"
      showHeader={false}
      transition={transition_time_default}
      data-testid="save-panel"
    >
      <Box className="save-report-panel">
        {/* HEADER */}
        <Box className="custom-header">
          <Box className="header-title-container">
            <Typography
              className="header-title"
              data-testid="save-report-title"
            >
              {t('saveReport.title')}
            </Typography>

            <ArrTooltipWrapper
              title={t('communicationExplorer.header.save.titleTooltiptitle')}
              trigger={
                <div tabIndex={0} data-testid={`save-communication-panel-div`}>
                  <InfoIcon
                    fontSize="medium"
                    className="view-user-info-icon"
                    data-testid="view-user-info-icon"
                  />
                </div>
              }
            >
              <p data-testid={`save-communication-panel-title-tooltip-content`}>
                {t('communicationExplorer.header.save.titleTooltipContent')}
              </p>

              <p data-testid={`save-communication-panel-title-tooltip-content1`}>
                {t('communicationExplorer.header.save.titleTooltipContent1')}
              </p>

              <p data-testid={`save-communication-panel-title-tooltip-content2`}>
                {t('communicationExplorer.header.save.titleTooltipContent2')}
              </p>

              <p data-testid={`save-communication-panel-title-tooltip-content3`}>
                {t('communicationExplorer.header.save.titleTooltipContent3')}
              </p>
            </ArrTooltipWrapper>
          </Box>

          <Box className="header-actions">
            <ArrButton
              variant="secondary"
              size="medium"
              onClick={closeModal}
              className="cancel-btn"
              dataTestId="cancel-btn"
            >
              {t('common.cancel')}
            </ArrButton>

            <ArrButton
              variant="primary"
              size="medium"
              className="save-btn"
              dataTestId="save-btn"
              disabled={
                action === SAVE_AS_NEW
                  ? reportName.trim() === ''
                  : !selectedItem
              }
              onClick={handleSave}
              loading={saveReportMutation.isPending}
            >
              {t('common.save')}
            </ArrButton>
          </Box>
        </Box>

        <Box className="form-section">
          <ArrRadio
          dataTestId="action"
            name="action"
            value={action}
            onChange={setAction}
            data={actionOptions}
            size="medium"
            data-testid="save-action-radio"
          />
        </Box>
        {/* SAVE AS NEW */}
        {action === SAVE_AS_NEW ? (
          <>
            <Box>
              <ArrTextField
                placeholder={t('saveReport.customReport')}
                value={reportName}
                onChange={(e) => {
                  setReportName(e.target.value);
                  if (showErrorToast) setShowErrorToast(false);
                }}
                sizeVariant="large"
                state="enabled"
                className="dark-input"
                label={t('saveReport.customReport')}
                title={t('saveReport.customReport')}
                dataTestId="save-comm-report-name-field"
              />
            </Box>

            <Box className="form-section report-type">
              <ArrRadio

                name="reportType"
                value={reportType}
                onChange={(val) => setReportType(val)}
                data={reportTypeOptions}
                size="medium"
                dataTestId="report-type-radio"
              />
            </Box>
          </>
        ) : (
          /** OVERWRITE → TREE VIEW */
          (<Box className="over-write-reports">
            <Typography
              variant="body2"
              className="form-label"
              data-testid={`save-communication-panel-select`}>
              {t('saveReport.select')}
            </Typography>
            <Box className="report-tree-view">
              <ArrTreeView
                setSelectedItem={handleSelected}
                itemsList={treeData}
                selectedItem={selectedItem}
                handleNoResultMessage={
                  isFetching
                    ? t('saveReport.loading')
                    : t('saveReport.noReports')
                }
                allowGlobalSelect={false}
                treeSelectedItems={[]}
                multipleSelection={false}
                isShowCountLabel={true}
                isShowCheckbox={false}
                data-testid="overwrite-treeview"
              />
            </Box>
          </Box>)
        )}
      </Box>
      {showSuccessToast && (
        <ArrToastNotification
          status={[ToastType.Success]}
          message={t('communicationExplorer.header.save.successMessage')}
          onClose={() => setShowSuccessToast(false)}
        />
      )}
      {showErrorToast && (
        <ArrToastNotification
          status={[ToastType.Error]}
          message={errorMessage}
          onClose={() => setShowErrorToast(false)}
        />
      )}
      <ArrModal
        showModal={showConfirmationModal}
        autoFocusCloseButton={true}
        className="modal-box"
        onClose={handleModalCancel}
        title={t('saveReport.modalTitle')}
        description={confirmationMessage}
        dataTestId="savereport-confirm-modal"
        buttons={[
          {
            id: 'no',
            label: t('common.no'),
            onClick: handleModalCancel,
            variant: 'secondary',
            dataTestId: 'confirm-save-no',
          },

          {
            id: 'yes',
            label: t('common.yes'),
            onClick: handleModalConfirmSave,
            variant: 'primary',
            dataTestId: 'confirm-save-yes',
          },
        ]}
      />
    </ArrDrawer>
  );
};

export default SaveCommunicationPanel;
